import { useState, useEffect } from "react";
import { MessageSquare, X, Send, ChevronLeft } from "lucide-react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ScrollArea } from "./ui/scroll-area";
import { Badge } from "./ui/badge";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChatButton = ({ listingId, listingTitle, ownerName }) => {
  const { user, token } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const startConversation = async () => {
    if (!user || !token) {
      toast.error("Trebuie să fii autentificat pentru a trimite mesaje");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/chat/conversations?listing_id=${listingId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConversation(response.data);
      fetchMessages(response.data.id);
    } catch (error) {
      console.error("Error starting conversation:", error);
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || "Nu poți începe această conversație");
      } else {
        toast.error("Eroare la inițializarea conversației");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (convId) => {
    try {
      const response = await axios.get(
        `${API}/chat/conversations/${convId}/messages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(response.data);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !conversation) return;

    setSending(true);
    try {
      const response = await axios.post(
        `${API}/chat/conversations/${conversation.id}/messages`,
        { content: newMessage, listing_id: listingId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages([...messages, response.data]);
      setNewMessage("");
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Eroare la trimiterea mesajului");
    } finally {
      setSending(false);
    }
  };

  const handleOpen = () => {
    if (!user) {
      toast.error("Trebuie să fii autentificat pentru a contacta proprietarul");
      return;
    }
    setIsOpen(true);
    if (!conversation) {
      startConversation();
    }
  };

  // Auto-refresh messages every 10 seconds when chat is open
  useEffect(() => {
    if (isOpen && conversation) {
      const interval = setInterval(() => {
        fetchMessages(conversation.id);
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [isOpen, conversation]);

  return (
    <>
      {/* Chat Button */}
      <Button
        data-testid="chat-button"
        onClick={handleOpen}
        variant="outline"
        className="gap-2 border-purple-200 text-purple-700 hover:bg-purple-50"
      >
        <MessageSquare className="w-4 h-4" />
        Contactează proprietarul
      </Button>

      {/* Chat Modal */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setIsOpen(false)}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl w-full max-w-md h-[500px] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-semibold text-sm truncate max-w-[200px]">
                    {listingTitle}
                  </p>
                  <p className="text-purple-200 text-xs">
                    {ownerName || "Proprietar"}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages Area */}
            <ScrollArea className="flex-1 p-4">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center text-stone-500">
                  <MessageSquare className="w-12 h-12 mb-3 text-stone-300" />
                  <p className="font-medium">Începe conversația</p>
                  <p className="text-sm">Trimite un mesaj proprietarului</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender_id === user?.id ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                          msg.sender_id === user?.id
                            ? "bg-purple-600 text-white rounded-br-md"
                            : "bg-stone-100 text-stone-800 rounded-bl-md"
                        }`}
                      >
                        <p className="text-sm">{msg.content}</p>
                        <p className={`text-xs mt-1 ${
                          msg.sender_id === user?.id ? "text-purple-200" : "text-stone-400"
                        }`}>
                          {new Date(msg.created_at).toLocaleTimeString("ro-RO", {
                            hour: "2-digit",
                            minute: "2-digit"
                          })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>

            {/* Input Area */}
            <form onSubmit={sendMessage} className="p-4 border-t border-stone-200 bg-stone-50">
              <div className="flex gap-2">
                <Input
                  data-testid="chat-input"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Scrie un mesaj..."
                  disabled={sending || !conversation}
                  className="flex-1"
                />
                <Button 
                  data-testid="chat-send-button"
                  type="submit" 
                  disabled={sending || !newMessage.trim()}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {sending ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatButton;
